import React, { useState, useEffect, useMemo } from "react";
import DataTable from "react-data-table-component";
import { Card, Button, Form, Row, Col, InputGroup } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { FaSearch, FaFileExcel, FaFilter, FaCalendarAlt, FaStore } from "react-icons/fa";

import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";
import styles from "../assets/css/Dashboard.module.css";

function Market() {
  // State for Market Data
  const [marketData, setMarketData] = useState([]);
  const [marketLoading, setMarketLoading] = useState(true);
  const [marketSearchTerm, setMarketSearchTerm] = useState("");
  const [marketStartDate, setMarketStartDate] = useState("");
  const [marketEndDate, setMarketEndDate] = useState("");

  const isChairman = localStorage.getItem("main_role") === "mainchairman";
  const API_URL = process.env.REACT_APP_SUPER_ADMIN_API_URL;
  const token = isChairman
    ? JSON.parse(localStorage.getItem("adminUser")).token
    : localStorage.getItem("token");

  // Fetch Market Data
  useEffect(() => {
    const fetchMarketData = async () => {
      setMarketLoading(true);
      try {
        const response = await axios.get(`${API_URL}/list-market-data.php`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.success && Array.isArray(response.data.data)) {
          setMarketData(response.data.data);
        }
      } catch (error) {
        toast.error("Failed to fetch market data.");
      } finally {
        setMarketLoading(false);
      }
    };
    fetchMarketData();
  }, [API_URL, token]);

  // Filtering and Memoization for Market Data
  const filteredMarketData = useMemo(() => {
    return marketData.filter((item) => {
      const matchesSearch = item.challan_no
        .toLowerCase()
        .includes(marketSearchTerm.toLowerCase());
      const itemDate = new Date(item.collection_date);
      const start = marketStartDate ? new Date(marketStartDate) : null;
      const end = marketEndDate ? new Date(marketEndDate) : null;

      if (start && itemDate < start) return false;
      if (end && itemDate > end) return false;

      return matchesSearch;
    });
  }, [marketData, marketSearchTerm, marketStartDate, marketEndDate]);

  const totalMarketAmount = useMemo(
    () =>
      filteredMarketData.reduce(
        (sum, item) => sum + parseFloat(item.amount || 0),
        0
      ),
    [filteredMarketData]
  );

  // Export Handler for Market Data
  const handleMarketExport = () => {
    const dataToExport = filteredMarketData.map((d) => ({
      "Challan No": d.challan_no,
      Amount: d.amount,
      "Total Coupons": d.total_of_coupon,
      "Collection Date": d.collection_date,
    }));
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "MarketData");
    XLSX.writeFile(workbook, "Market_Data_Report.xlsx");
  };

  // Columns Definition for Market Data
  const marketColumns = [
    { name: "Challan No", selector: (row) => row.challan_no, sortable: true },
    {
      name: "Amount (₹)",
      selector: (row) => parseFloat(row.amount).toLocaleString("en-IN"),
      sortable: true,
    },
    {
      name: "Total Coupons",
      selector: (row) => row.total_of_coupon,
      sortable: true,
    },
    {
      name: "Collection Date",
      selector: (row) => row.collection_date,
      sortable: true,
    },
  ];

  // Custom styles for DataTable
  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#f8f9fa",
        borderBottom: "1px solid #dee2e6",
        fontSize: "0.875rem",
        fontWeight: "500",
        color: "#495057",
      },
    },
    rows: {
      style: {
        fontSize: "0.875rem",
        color: "#212529",
        "&:hover": {
          backgroundColor: "#f8f9fa",
        },
      },
    },
    cells: {
      style: {
        padding: "12px 16px",
      },
    },
  };

  return (
    <>
      <Topbar />
      <div className="d-flex">
        <Sidebar />
        <div className={styles.dashboardContainer}>
          <div className={styles.dashboardContent}>
            {/* Header Section */}
            <div className="d-flex align-items-center justify-content-between mb-4">
              <div className="d-flex align-items-center">
                <FaStore className="text-primary me-3" style={{ fontSize: "2rem" }} />
                <h2
                  className="mb-0"
                  style={{
                    color: "#2c3e50",
                    fontWeight: "500",
                    fontSize: "1.5rem",
                  }}
                >
                  Market Data Management
                </h2>
              </div>
            </div>

            {/* Market Data Section */}
            <div className="card" style={{ borderRadius: "8px", backgroundColor: "#fff" }}>
              <div className="card-body">
                {/* Filter Section */}
                <div className="mb-4">
                  <div className="d-flex align-items-center mb-3">
                    <FaFilter className="text-primary me-2" style={{ fontSize: "0.875rem" }} />
                    <span className="text-muted" style={{ fontSize: "0.875rem" }}>
                      Filter Options
                    </span>
                  </div>
                  <Row className="g-3 align-items-end">
                    <Col md={4}>
                      <Form.Label className="small text-muted mb-1">Search by Challan No</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="search"
                          placeholder="Enter challan no..."
                          value={marketSearchTerm}
                          onChange={(e) => setMarketSearchTerm(e.target.value)}
                          style={{ borderColor: "#dee2e6" }}
                        />
                      </InputGroup>
                    </Col>
                    <Col md={3}>
                      <Form.Label className="small text-muted mb-1">Start Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={marketStartDate}
                        onChange={(e) => setMarketStartDate(e.target.value)}
                        style={{ borderColor: "#dee2e6" }}
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label className="small text-muted mb-1">End Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={marketEndDate}
                        onChange={(e) => setMarketEndDate(e.target.value)}
                        style={{ borderColor: "#dee2e6" }}
                      />
                    </Col>
                    <Col md={2} className="text-md-end">
                      <Button onClick={handleMarketExport} variant="success" size="sm">
                        <FaFileExcel className="me-2" />
                        Export
                      </Button>
                    </Col>
                  </Row>
                </div>

                {/* Summary Cards */}
                <Row className="mb-4 text-center">
                  <Col md={4}>
                    <Card bg="light" style={{ borderRadius: "8px" }}>
                      <Card.Body>
                        <Card.Title className="text-muted small mb-2">Total Collection</Card.Title>
                        <Card.Text className="fs-4 fw-bold text-primary">
                          ₹{totalMarketAmount.toLocaleString("en-IN")}
                        </Card.Text>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card bg="light" style={{ borderRadius: "8px" }}>
                      <Card.Body>
                        <Card.Title className="text-muted small mb-2">Total Records</Card.Title>
                        <Card.Text className="fs-4 fw-bold text-info">
                          {filteredMarketData.length}
                        </Card.Text>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card bg="light" style={{ borderRadius: "8px" }}>
                      <Card.Body>
                        <Card.Title className="text-muted small mb-2">Average Amount</Card.Title>
                        <Card.Text className="fs-4 fw-bold text-success">
                          ₹{filteredMarketData.length > 0 
                            ? (totalMarketAmount / filteredMarketData.length).toLocaleString("en-IN", { maximumFractionDigits: 2 })
                            : "0"
                          }
                        </Card.Text>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {/* Data Table */}
                <DataTable
                  columns={marketColumns}
                  data={filteredMarketData}
                  pagination
                  responsive
                  progressPending={marketLoading}
                  customStyles={customStyles}
                  progressComponent={
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  }
                  noDataComponent={
                    <div className="text-center py-4 text-muted">
                      No market data found
                    </div>
                  }
                />
              </div>
            </div>

            {/* Report Date Range */}
            <div
              className="mt-3 d-flex align-items-center text-muted"
              style={{ fontSize: "0.875rem" }}
            >
              <FaCalendarAlt className="me-2" />
              Report generated on: {new Date().toLocaleDateString()}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Market;